import * as React from "react";
import { WizardLXPWAProps } from "./typings";
import { Location } from "src/components/shared/Location/Location";
import { observer, MobXProviderContext } from "mobx-react";
import { LobTitle } from "src/components/shared/LobTitle/LobTitle";
import { SubmitRow } from "src/components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { Dates } from "src/components/shared/Dates/Dates";
import { ClientWizardStateStore } from "stores/wizard/ClientWizardStateStore";
import { LobSubtitleWithLink } from "src/components/shared/LobSubtitle/LobSubtitleWithLink";
import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { LobErrorSummary } from "src/components/shared/LobErrorSummary/LobErrorSummary";
import { LobSubtitle } from "src/components/shared/LobSubtitle/LobSubtitle";
import { getShoppingGlobalMicroMessage } from "components/shared/StorefrontWizard/SubmitRow/UISShoppingGlobal";
import { SubmitButton } from "components/shared/SubmitButton/SubmitButton";
import { UitkSpacing } from "uitk-react-spacing";
import { Columns } from "components/shared/StorefrontWizard/typings";

export const WizardLXPWAView: React.FunctionComponent<WizardLXPWAProps> = observer(({ flexComponent }) => {
  const wizardState: ClientWizardStateStore = React.useContext(MobXProviderContext).wizardState;
  const {
    globalWizardState,
    lxWizardState,
    lxWizardState: { config: lxConfig },
    lxWizardState: {
      config: { heading },
    },
  } = wizardState;

  const { halfWizard } = wizardState.globalWizardState.config;
  const { searchButtonAlongsideDates = false } = flexComponent.model;

  const locationColumns: Columns = {
    fullWidth: {
      small: 1,
      medium: searchButtonAlongsideDates ? 5 : 7,
    },
    half: {
      small: 1,
      medium: 6,
      large: 6,
    },
  };

  const datesColumns: Columns = {
    fullWidth: {
      small: 1,
      large: 5,
    },
    half: {
      small: 1,
      medium: 6,
      large: 6,
    },
  };

  const submitColumns: Columns = {
    fullWidth: {
      small: 1,
      medium: 2,
    },
    half: {
      small: 1,
      medium: 2,
    },
  };

  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 12 };
  const locationColSpan: UitkLayoutConditionalGridSpan = locationColumns[halfWizard ? "half" : "fullWidth"];
  const datesColSpan: UitkLayoutConditionalGridSpan = datesColumns[halfWizard ? "half" : "fullWidth"];
  const submitColSpan: UitkLayoutConditionalGridSpan = submitColumns[halfWizard ? "half" : "fullWidth"];

  const { visibleLobSubtitle, locSubtitleTextToken, locSubtitleLinkToken, locSubtitleLinkUrl } = heading;
  const uisShoppingGlobal = getShoppingGlobalMicroMessage(lxWizardState);

  const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
    lxWizardState.wizardInputsArray.push(ref);
    if (secondRef) {
      lxWizardState.wizardInputsArray.push(secondRef);
    }
  };

  const getLocError = (isLink: boolean) => {
    if (!isLink) {
      return lxWizardState.moreThanOneError()
        ? lxConfig.form.submitButton.locMultipleWarningsToken
        : lxConfig.form.submitButton.locWarningToken;
    }

    return lxWizardState.moreThanOneError()
      ? lxConfig.form.submitButton.locMultipleWarningsLinkToken
      : lxConfig.form.submitButton.locWarningLinkToken;
  };

  return (
    <form
      noValidate={true}
      action={lxConfig.form.action}
      autoComplete="off"
      className="WizardLXPWA uitk-cell"
      id={lxConfig.elementId}
      data-fm={flexComponent.fmId}
      data-fm-title-id={flexComponent.fmTitleId}
      data-testid="wizard"
      onSubmit={lxWizardState.submit}
    >
      {!halfWizard && <LobTitle lobState={lxWizardState} />}
      {!halfWizard && <LobSubtitle lobState={lxWizardState} />}
      {!halfWizard && visibleLobSubtitle && (
        <UitkSpacing padding={{ block: "three" }}>
          <LobSubtitleWithLink
            locKey={locSubtitleTextToken!}
            locLinkKey={locSubtitleLinkToken!}
            linkUrl={locSubtitleLinkUrl!}
          />
        </UitkSpacing>
      )}
      <UitkSpacing padding={{ blockstart: "three", blockend: "three" }}>
        <UitkLayoutGrid columns={mainGridColumns} space="three">
          <>
            {lxWizardState.numberOfErrors > 0 && (
              <LobErrorSummary
                locHeadingToken={getLocError(false)!}
                locHeadingArgs={lxWizardState.moreThanOneError() && [lxWizardState.numberOfErrors]}
                locLinkToken={getLocError(true)}
                linkClickFocusId="id-roundtrip-error"
                inputErrorRef={lxWizardState.errorInputRef}
                colSpan={mainGridColumns}
                setInputsRefs={setInputsRefs}
              />
            )}
            <Location
              setInputsRefs={setInputsRefs}
              originField={false}
              destinationField={true}
              lobState={lxWizardState}
              colSpan={locationColSpan}
              globalConfig={globalWizardState.config}
            />
            <Dates
              lobState={lxWizardState}
              colSpan={datesColSpan}
              singleDateSelect={true}
              setInputsRefs={setInputsRefs}
            />
            {searchButtonAlongsideDates && (
              <SubmitButton
                lobState={lxWizardState}
                colSpan={submitColSpan}
                rfrr={"SearchClick_LX"}
                uisPrimeMicroMessages={uisShoppingGlobal}
              />
            )}
          </>
        </UitkLayoutGrid>
      </UitkSpacing>
      {!searchButtonAlongsideDates && (
        <SubmitRow lobState={lxWizardState} rfrr={"SearchClick_LX"} globalConfig={globalWizardState.config} />
      )}
    </form>
  );
});

export default WizardLXPWAView;
